import { PermissionBit, PermissionMetadataItem } from "../../../api";

/**
 * Presentation grouping for the permissions dialog. The groups are the ones the
 * metadata endpoint already serves - `permissions` arrives keyed by category,
 * so nothing here re-derives them - ordered so the routers read as the choice
 * at the top and the fine-tuning follows.
 *
 * Category strings come from the backend and are matched loosely: a category
 * matching none of the known names is rendered last under its own heading
 * rather than silently dropped.
 */
const GROUP_ORDER = ["router", "operation", "monitoring", "advanced"];

const VISIBILITY_BITS = new Set<number>([
  PermissionBit.VISIBILITY_SELF,
  PermissionBit.VISIBILITY_MANAGED,
  PermissionBit.VISIBILITY_REGIONS,
  PermissionBit.VISIBILITY_CLINICS,
  PermissionBit.VISIBILITY_PHYSICIAN,
  PermissionBit.VISIBILITY_FULL,
]);

export interface PermissionGroup {
  name: string;
  items: PermissionMetadataItem[];
}

/**
 * True for the group belonging in the visibility column. Matches the bits as
 * well as the category name, so the column still finds them if the backend
 * renames the category to something `visib` no longer matches.
 */
export function isVisibilityGroup(group: PermissionGroup): boolean {
  return (
    /visib/i.test(group.name) ||
    group.items.some((item) => VISIBILITY_BITS.has(item.bit))
  );
}

/** The endpoint's own category grouping, in {@link GROUP_ORDER}. */
export function orderGroups(permissions: {
  [category: string]: PermissionMetadataItem[];
}): PermissionGroup[] {
  // Unknown categories sort after the known ones; a stable sort leaves them in
  // the order the API listed them in.
  const rank = (name: string) => {
    const i = GROUP_ORDER.findIndex((known) =>
      name.toLowerCase().includes(known),
    );
    return i === -1 ? GROUP_ORDER.length : i;
  };

  return Object.entries(permissions)
    .map(([name, items]) => ({ name, items }))
    .sort((a, b) => rank(a.name) - rank(b.name));
}
